import { Rectangle, Texture } from 'pixi.js';
import { corsUrl, loadBitmap, type AssetCatalog } from './assets';
import { PARTICLE_SHAPE_CLASS, PARTICLE_SHAPE_ORIGIN } from './particle-shapes';

interface RawFrame {
  frame: { x: number; y: number; w: number; h: number };
  spriteSourceSize?: { x: number; y: number; w: number; h: number };
  sourceSize?: { w: number; h: number };
}
interface RawAtlas {
  frames: Record<string, RawFrame>;
  animations?: Record<string, string[]>;
}

/** Forma de partícula pronta: a textura e o ponto do quadro onde fica a origem do símbolo (fração do quadro inteiro). */
export interface ParticleShape {
  texture: Texture;
  anchor: { x: number; y: number };
}

const SHAPE_ATLAS = 'atlas/swf/Resource/partical/shape/Resource_partical_shape';

/**
 * As formas das partículas, já convertidas do `shape.swf` do legado (`ParticalShap<n>`; `displayCreator` é o índice em `PARTICLE_SHAPE_CLASS`). O atlas vem uma vez só.
 * Enquanto baixa, `peek` devolve `null` e as partículas dessa forma não aparecem; forma que o atlas não tem também não aparece.
 */
export class ParticleArtLibrary {
  private atlas: { raw: RawAtlas; source: Texture['source'] } | null = null;
  private loading: Promise<void> | null = null;
  private readonly shapes = new Map<number, ParticleShape | null>();

  constructor(private readonly assets: AssetCatalog) {}

  /** Baixa o atlas uma vez; quem chega depois recebe a mesma promessa. Nunca rejeita. */
  preload(): Promise<void> {
    this.loading ??= this.load().catch(() => undefined);
    return this.loading;
  }

  private async load(): Promise<void> {
    const keys = new Map(Object.keys(this.assets.manifest.assets).map((key) => [key.toLowerCase(), key]));
    const json = keys.get(`${SHAPE_ATLAS}.json`.toLowerCase());
    const webp = keys.get(`${SHAPE_ATLAS}.webp`.toLowerCase());
    if (!json || !webp) return;
    const response = await fetch(corsUrl(this.assets.url(json)));
    if (!response.ok) return;
    const sheet = await loadBitmap(this.assets.url(webp));
    if (!sheet) return;
    this.atlas = { raw: (await response.json()) as RawAtlas, source: Texture.from(sheet).source };
  }

  /** A forma `shape` (o `displayCreator` do emissor) ou `null` se o atlas ainda não chegou ou não a tem. */
  peek(shape: number): ParticleShape | null {
    void this.preload();
    if (!this.atlas) return null;
    const cached = this.shapes.get(shape);
    if (cached !== undefined) return cached;
    const number = PARTICLE_SHAPE_CLASS[shape];
    if (number === undefined) {
      // Índice fora da lista: o legado desenha um círculo preto, invisível em modo aditivo.
      this.shapes.set(shape, null);
      return null;
    }
    const name = this.atlas.raw.animations?.[`ParticalShap${number}`]?.[0] ?? `ParticalShap${number}/1`;
    const f = this.atlas.raw.frames[name];
    let built: ParticleShape | null = null;
    if (f) {
      const full = f.sourceSize ?? { w: f.frame.w, h: f.frame.h };
      const offset = f.spriteSourceSize ?? { x: 0, y: 0, w: f.frame.w, h: f.frame.h };
      const texture = new Texture({
        source: this.atlas.source,
        frame: new Rectangle(f.frame.x, f.frame.y, f.frame.w, f.frame.h),
        orig: new Rectangle(0, 0, full.w, full.h),
        trim: new Rectangle(offset.x, offset.y, f.frame.w, f.frame.h),
      });
      // A origem vem do SWF. Sem ela (forma fora da tabela), o centro do quadro.
      const origin = PARTICLE_SHAPE_ORIGIN[number];
      built = { texture, anchor: origin ? { x: origin[0] / full.w, y: origin[1] / full.h } : { x: 0.5, y: 0.5 } };
    }
    this.shapes.set(shape, built);
    return built;
  }
}
